import * as React from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { currency } from './ui';

export const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', online: 'Pagado en línea', balance: 'Saldo',
};

const PAYMENT_COLORS: Record<string, { bg: string; fg: string }> = {
  cash: { bg: 'rgba(46,125,50,0.10)', fg: '#2e7d32' },
  card: { bg: 'rgba(21,101,192,0.10)', fg: '#1565c0' },
  balance: { bg: 'rgba(161,98,7,0.12)', fg: '#a16207' },
  online: { bg: 'rgba(106,27,154,0.10)', fg: '#6a1b9a' },
};

const BRANDS: Record<string, string> = {
  visa: 'Visa', master: 'Mastercard', mastercard: 'Mastercard', amex: 'Amex', debvisa: 'Visa débito', debmaster: 'Mastercard débito',
  account_money: 'Cuenta de Mercado Pago',
};
export const brandLabel = (brand?: string | null) => (brand ? BRANDS[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Tarjeta');

/** A sale's payment as the server reports it (api/receipts/): the card charged and what the customer's saldo paid. */
export type SalePayments = {
  payment?: string | null;
  payments?: { method: string; provider: string; amount: number; card_brand: string | null; card_last_four: string | null }[];
  balance_used?: number;
};

/** How a sale was paid: its method, the card (brand and last four) and the saldo it used. */
export const PaymentCell = ({ sale }: { sale: SalePayments }) => {
  const color = PAYMENT_COLORS[sale.payment ?? ''] ?? { bg: 'rgba(20,37,44,0.06)', fg: '#14252C' };
  const card = (sale.payments ?? []).find(p => p.card_brand || p.card_last_four);
  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      {sale.payment
        ? <Chip size="small" label={PAYMENT_LABELS[sale.payment] ?? sale.payment} sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }} />
        : <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>}
      {card && (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }} noWrap>
          {brandLabel(card.card_brand)}{card.card_last_four ? ` ···${card.card_last_four}` : ''}
        </Typography>
      )}
      {!!sale.balance_used && sale.payment !== 'balance' && (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }} noWrap>Saldo {currency(sale.balance_used)}</Typography>
      )}
    </Stack>
  );
};

/** A sold line with its options and note: "Latte (Grande, Avena)". */
export const itemLabel = (item: { product?: { name?: string } | null; name?: string | null; notes?: string | null;
  modifiers?: { name: string }[] }) => {
  const extras = [...(item.modifiers ?? []).map(m => m.name), item.notes].filter(Boolean).join(', ');
  return `${item.product?.name ?? item.name ?? 'Producto'}${extras ? ` (${extras})` : ''}`;
};
